import React from 'react';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import FormLabel from '@mui/material/FormLabel';
import Typography from '@mui/material/Typography';
import theme from 'styles/theme';
import { Stack } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import { TextError } from './TextError';
import 'dayjs/locale/en-gb';

interface CustomDatePickerProps {
  value: Dayjs | null;
  onChange: (date: Dayjs | null) => void;
  title: string;
  hasTime?: boolean;
  noPlaceholder?: boolean;
  datePickerProps?: Partial<DatePickerProps<Dayjs, boolean>>;
  inputError?: string;
  className?: string;
  placeholder?: string;
  maxDate?: Dayjs;
  minDate?: Dayjs;
}

export const CustomDatePicker = ({
  value,
  onChange,
  title,
  hasTime = false,
  datePickerProps = {},
  placeholder,
  inputError,
  maxDate,
  noPlaceholder,
  minDate
}: CustomDatePickerProps) => {
  const Picker = hasTime ? DateTimePicker : DatePicker;

  return (
    <LocalizationProvider adapterLocale="en-gb" dateAdapter={AdapterDayjs}>
      <Stack gap={1}>
        {title && (
          <FormLabel>
            <Typography fontSize="13px" fontWeight={500} color={theme.colors.dark1}>
              {title}
            </Typography>
          </FormLabel>
        )}
        <Picker
          value={value}
          /* eslint-disable @typescript-eslint/no-explicit-any */
          onChange={onChange as any}
          maxDate={maxDate || dayjs('2099/12/31', 'YYYY/MM/DD')}
          minDate={minDate}
          format={hasTime ? 'YYYY/MM/DD HH:mm' : 'YYYY/MM/DD'}
          ampm={hasTime ? false : undefined}
          label={placeholder}
          slotProps={
            {
              textField: {
                inputProps: {
                  placeholder: noPlaceholder ? '' : hasTime ? 'YYYY/MM/DD HH:mm' : 'YYYY/MM/DD'
                }
              }
              /* eslint-disable @typescript-eslint/no-explicit-any */
            } as any
          }
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              border: 'none',
              color: '#000',
              height: '55px',
              overflow: 'hidden',
              '&.Mui-error fieldset': {
                borderColor: 'red'
              },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#000',
                color: '#000'
              },
              '& .MuiInputBase-input': {
                color: '#000'
              }
            },
            '& .MuiInputBase-root': {
              '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {},
              '&.Mui-disabled': {
                color: '#000',
                WebkitTextFillColor: '#000'
              }
            },
            '.MuiFormLabel-root.Mui-focused': {
              display: 'none'
            },
            '.MuiFormLabel-root.MuiFormLabel-filled': {
              display: 'none'
            },
            '.MuiFormLabel-root': {
              marginBottom: '8px',
              opacity: '0.6',
              top: '-5px'
            },
            '& fieldset': {
              border: inputError ? '1px solid #ff1744' : '1px solid #ccc',
              borderRadius: '11px',
              top: 0,
              '& legend': {
                display: 'none'
              }
            }
          }}
          /* eslint-disable @typescript-eslint/no-explicit-any */
          {...(datePickerProps as any)}
        />

        {inputError && (
          <FormHelperText error sx={{ ml: 0, maxWidth: '100%' }}>
            <TextError errorText={inputError} />
          </FormHelperText>
        )}
      </Stack>
    </LocalizationProvider>
  );
};

export default CustomDatePicker;
